import { ReactNode } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { Box, Panel, StandardLayout } from '@sequencehq/core-components'

import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import PageError from 'components/ErrorPages/PageError'
import { useIsMobile } from 'hooks/useIsMobile'

/**
 * The inbox's two panes, laid out for the viewport:
 *
 *   desktop:  [ list | detail ]
 *   mobile:   [ list ]  or  [ detail ]   -- whichever the route selects
 *
 * `detailRoutePatterns` are the routes that mean "an item is open", so mobile
 * knows to show the detail instead of the list. Both panes stay mounted either
 * way; mobile only hides one.
 */
export const InboxPanes = ({
  list,
  detail,
  detailRoutePatterns
}: {
  list: ReactNode
  detail: ReactNode
  detailRoutePatterns: Array<string>
}) => {
  const location = useLocation()
  const isMobile = useIsMobile()

  const hasSelectedDetail = detailRoutePatterns.some(pattern =>
    matchPath(pattern, location.pathname)
  )

  if (isMobile) {
    return (
      // Mobile shows one pane at a time (no SplitContent), so wrap it in a
      // Panel to give the single pane the same white surface SplitContent's
      // panel provides on desktop — otherwise the list side, which has no
      // background of its own, shows the gray backdrop. Panel is full-bleed on
      // mobile (no border/radius), so this reads as an edge-to-edge white page.
      <Panel height="100%" width="100%">
        {/* Both stay mounted, one shown: only the list notices the open task
            leaving. `display: none` drops the hidden pane from the tab order. */}
        <Box height="100%" display={hasSelectedDetail ? 'none' : 'block'}>
          <ErrorBoundary name="WatchtowerInboxList" fallback={PageError}>
            {list}
          </ErrorBoundary>
        </Box>
        <Box height="100%" display={hasSelectedDetail ? 'block' : 'none'}>
          <ErrorBoundary name="WatchtowerInboxPreview" fallback={PageError}>
            {detail}
          </ErrorBoundary>
        </Box>
      </Panel>
    )
  }

  return (
    <StandardLayout.SplitContent
      columns="auto 1fr"
      leadingSide={list}
      trailingSide={
        <ErrorBoundary name="WatchtowerInboxPreview" fallback={PageError}>
          {detail}
        </ErrorBoundary>
      }
    />
  )
}
